/**
 * Assainisseur HTML de l'application, adossé à DOMPurify.
 *
 * Défense en profondeur pour du HTML semi-confiance (référentiels produits,
 * templates d'emails servis par notre back) — et robuste aussi face à du
 * contenu hostile : DOMPurify est la référence du domaine (mXSS, espaces de
 * noms SVG/MathML, contournements exotiques suivis en continu).
 *
 * L'API par profils est à nous : les appelants ne voient jamais DOMPurify,
 * l'implémentation reste remplaçable en une seule fonction.
 */

import DOMPurify from 'dompurify';

export interface ProfilSanitize {
  balises: readonly string[];
  attributs: readonly string[];
  /** Schémas d'URL admis dans href/src. */
  urls: RegExp;
}

/* DOMPurify applique ce filtre à TOUTES les valeurs d'attributs : on
   autorise nos schémas d'URL, plus toute valeur qui ne ressemble pas à un
   schéma (`_blank`, `600`, chemins relatifs) — même philosophie que son
   défaut, en plus strict sur les schémas. */
const URL_SURE = /^(https?:|mailto:|tel:|#|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;
const URL_EMAIL = /^(https?:|mailto:|tel:|#|data:image\/(?:png|jpe?g|gif|webp);|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/* balises dont le CONTENU ne doit jamais survivre (le texte d'un <script>
   n'est pas du contenu) ; les balises simplement inconnues sont déballées */
const CONTENU_INTERDIT = [
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button',
  'textarea', 'select', 'noscript', 'template', 'audio', 'video', 'source', 'title',
  'svg', 'math', /* espaces de noms étrangers : rien n'en survit */
];

const TEXTE = [
  'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'sub', 'sup', 'small',
  'span', 'div', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'hr', 'a',
];

/** Descriptions riches (garanties, référentiels) : texte mis en forme. */
export const PROFIL_DESCRIPTION: ProfilSanitize = {
  balises: TEXTE,
  attributs: ['class', 'href', 'target', 'rel'],
  urls: URL_SURE,
};

/** Valeur d'une variable éditée au clavier : mise en forme minimale. */
export const PROFIL_VARIABLE: ProfilSanitize = {
  balises: ['b', 'strong', 'i', 'em', 'u', 'br', 'span', 'p', 'div'],
  attributs: [],
  urls: URL_SURE,
};

/** Templates d'emails : mise en page tableau, images, styles inline, et le
 * <style> d'en-tête (du CSS, pas du code) pour garder l'aperçu fidèle. */
export const PROFIL_EMAIL: ProfilSanitize = {
  balises: [
    ...TEXTE,
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
    'img', 'font', 'center', 'style',
  ],
  attributs: [
    'class', 'style', 'align', 'valign', 'width', 'height', 'border',
    'cellpadding', 'cellspacing', 'bgcolor', 'color',
    'href', 'target', 'rel', 'src', 'alt', 'face', 'size', 'colspan', 'rowspan',
  ],
  urls: URL_EMAIL,
};

/* une image dont la source a été refusée n'a plus de raison d'être */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'IMG' && !node.getAttribute('src')) node.remove();
});

export function sanitizeHtml(html: string, profil: ProfilSanitize = PROFIL_DESCRIPTION): string {
  if (!html) return '';
  /* un template email complet porte son CSS dans <head> : DOMPurify ne garde
     que le corps, on remonte donc les <style> d'en-tête dans le fragment */
  if (profil.balises.includes('style') && /<head[\s>]/i.test(html)) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const styles = Array.from(doc.head.querySelectorAll('style'))
      .map(st => `<style>${st.textContent ?? ''}</style>`)
      .join('');
    html = styles + doc.body.innerHTML;
  }
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...profil.balises],
    ALLOWED_ATTR: [...profil.attributs],
    ALLOWED_URI_REGEXP: profil.urls,
    /* déballer les balises inconnues (leur texte survit), mais jamais le
       contenu des balises actives ci-dessous — sauf si le profil les admet
       explicitement (le <style> des templates d'emails) */
    FORBID_CONTENTS: CONTENU_INTERDIT.filter(b => !profil.balises.includes(b)),
    KEEP_CONTENT: true,
    FORCE_BODY: true, /* sinon un <style> en tête de fragment est hissé dans <head> et perdu */
  });
}
